<script lang="ts" setup>
import { SancareHeader } from '@sancare/typescript-commons'
import { ComponentSize, FindBarColor } from '@sancare/typescript-commons/src/components/types'
import _ from 'lodash'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import logoImage from '@/assets/sancare_logo.png'
import FindBar from '@/common/FindBar.vue'
import PmsiTypeSelector from '@/common/PmsiTypeSelector.vue'
import { hasPermission, RoleEnum } from '@/misc/permissions/permissionsHelper'
import router from '@/router'
import UserSettings from '@/settings/UserSettings.vue'
import { useStore } from '@/store'
import { ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()
const route = useRoute()
const { t } = useI18n()

const menuItems = computed(() => {
  const sections = []
  const headerSection = []
  headerSection.push({
    icon: 'question',
    id: 'header-menu-about',
    label: t('header.menuBar.about'),
    onClick: () => router.push({
      name: 'about',
    })
  })
  sections.push({ items: headerSection })

  const menuSection = []
  if (hasPermission(RoleEnum.ROLE_MAINTENANCE_VIEWER, userRole.value)) {
    menuSection.push({
      icon: 'pulse',
      id: 'header-menu-maintenance',
      label: t('header.menuBar.maintenance'),
      onClick: () => router.push({
        name: 'maintenance-home',
        params: { pmsiType: pmsiType.value }
      })
    })
  }

  if (hasPermission(RoleEnum.ROLE_DASHBOARD_ACCESSOR, userRole.value)) {
    menuSection.push({
      icon: 'graph',
      id: 'header-menu-statistics',
      isDisabled: pmsiType.value === PmsiTypeEnum.SMR,
      label: t('header.menuBar.statistics'),
      tooltip: pmsiType.value === PmsiTypeEnum.SMR
        ? t('settings.tooltips.notAvailableStatistics')
        : null
      ,
      onClick: () => pmsiType.value === PmsiTypeEnum.MCO
        ? router.push({
          name: 'stats',
          query: { ...settingsUrlQuery.value }
        })
        : () => {}
    })
  }

  if (hasPermission(RoleEnum.ROLE_ADMIN_MANAGER, userRole.value)
      || hasPermission(RoleEnum.ROLE_USER_MANAGER, userRole.value)
      || hasPermission(RoleEnum.ROLE_SETTING_UPDATER, userRole.value)) {
    menuSection.push({
      icon: 'tools',
      id: 'header-menu-admin',
      label: t('header.menuBar.adminZone'),
      onClick: () => router.push({ name: 'admin' })
    })
  }

  menuSection.push({
    icon: 'person',
    id: 'header-menu-user-settings',
    label: t('header.menuBar.userSettings'),
    onClick: () => openUserSettings()
  })

  if (menuSection.length > 0) {
    sections.push({ items: menuSection })
  }

  const footerSection = []
  footerSection.push({
    icon: 'sign-out',
    label: t('header.menuBar.logout'),
    onClick: async () => {
      await store.dispatch('logout')
      await router.push({ name: 'login' })
    }
  })
  sections.push({ items: footerSection })

  return sections
})

const userSettingsRef = ref<{ openModal: () => void } | null>(null)

const settingsUrlQuery = computed(() => store.getters.settingsUrlQuery)
const pmsiType = computed(() => store.getters.pmsiType)
const isSmrEnabled = computed(() => store.state.settings.sancareSettings.boolSettings ? store.state.settings.sancareSettings.boolSettings.enable_smr.value : false)
const stayId = computed(() => route.params.stayId)
const userRole = computed(() => store.state.login.userRole)

const homeLink = computed(() => {
  if (userRole.value === 'ROLE_DATA_PROVIDER') {
    return { path: '/manage-api-key' }
  }

  return {
    name: 'home',
    params: { pmsiType: pmsiType.value },
    query: {
      mode: route.query.mode ?? ModeEnum.OPTIMIZABLE,
      cu: sessionStorage.getItem('stats-unit') ?? 'month',
      ...settingsUrlQuery.value,
    },
  }
})

function handleSearch({ result, input }) {
  const stayIdInput = !_.isEmpty(result) ? result.id : input
  const isCurrentStay = route.query.stayId !== undefined && String(stayIdInput) === String(route.query.stayId)
  // new-coding doesn't exist in SMR, its equivalent for a stay without initial coding is quality-stays
  const modeWithoutInitialCoding = pmsiType.value === PmsiTypeEnum.SMR ? ModeEnum.QUALITY_STAYS : ModeEnum.NEW_CODING
  const query = _.isEmpty(result)
    ? {}
    : isCurrentStay && route.query.mode
      ? { mode: route.query.mode }
      : { mode: result.hasInitialCoding ? ModeEnum.OPTIMIZABLE : modeWithoutInitialCoding }

  router.push({
    name: `${pmsiType.value}-stay`,
    params: {
      idType: 'id',
      stayId: stayIdInput,
    },
    query: query,
  })
}

const openUserSettings = () => userSettingsRef.value?.openModal()
</script>

<template>
  <div class="search-bar">
    <sancare-header
      :logo="logoImage"
      title="SANCARE"
      subtitle="Codage"
      :link="homeLink"
      :menu-items="menuItems"
    >
      <template #content-middle>
        <div
          v-if="hasPermission(RoleEnum.ROLE_STAY_READER, userRole)"
          class="col-3"
        >
          <find-bar
            id="header-find-bar"
            :default-search-value="stayId"
            class="w-100"
            :full-width="false"
            :color="FindBarColor.PURPLE"
            :size="ComponentSize.SMALL"
            @handle-submit="handleSearch"
            @handle-click="handleSearch"
          />
        </div>
      </template>
      <template #content-left>
        <pmsi-type-selector v-if="isSmrEnabled" />
      </template>
    </sancare-header>
    <user-settings ref="userSettingsRef" />
  </div>
</template>
